export type ApplicationStatus = "open" | "closed" | "not-open";

export type ApplicationWindow = {
  status: ApplicationStatus;
  opensOn?: string;
  closesOn?: string;
};

export type EventVenue = {
  name: string;
  streetAddress: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: "US";
};

export type SeoImage = {
  path: `/${string}`;
  width: number;
  height: number;
  alt: string;
};

export type SeoImageSet = {
  square: SeoImage;
  standard: SeoImage;
  wide: SeoImage;
};

export type RegistrationOffer = {
  name: string;
  totalPriceCents: number;
  currency: "USD";
  availability: "InStock" | "SoldOut" | "PreOrder";
  url: `https://${string}`;
  validFrom?: string;
  validThrough?: string;
};

export type EventConfig = {
  year: number;
  name: string;
  description: string;
  scheduleUrl?: `https://${string}`;
  timeZone: string;
  eventStatus: "EventScheduled" | "EventCancelled" | "EventPostponed" | "EventRescheduled";
  contentReviewedOn: string;
  seoImages: SeoImageSet;
  registrationOffers: readonly RegistrationOffer[];
  publicDates: {
    startsOn: string;
    endsOn: string;
    startsAt: string;
    endsAt: string;
    label: string;
  };
  attendance: {
    minimum: number;
    label: string;
  };
  venues: {
    primary: EventVenue;
    secondary: EventVenue;
  };
  applications: {
    artistAlley: ApplicationWindow;
    panelist: ApplicationWindow;
    vendor: ApplicationWindow;
    volunteer: ApplicationWindow;
  };
};

const EVENT_CONFIG_2026 = {
  year: 2026,
  name: "Matsuricon 2026",
  description:
    "Matsuricon 2026 is Columbus, Ohio's three-day anime convention, bringing 17,000+ fans together for anime, gaming, cosplay, and Japanese pop culture.",
  scheduleUrl: "https://www.eventeny.com/share/?s=cvRwNvVY23176",
  timeZone: "America/New_York",
  eventStatus: "EventScheduled",
  contentReviewedOn: "2026-08-06",
  seoImages: {
    square: {
      path: "/event-images/square",
      width: 1200,
      height: 1200,
      alt: "Matsuricon 2026 convention social card",
    },
    standard: {
      path: "/event-images/standard",
      width: 1200,
      height: 900,
      alt: "Matsuricon 2026 convention social card",
    },
    wide: {
      path: "/event-images/wide",
      width: 1200,
      height: 630,
      alt: "Matsuricon 2026 convention social card",
    },
  },
  registrationOffers: [
    {
      name: "Full Weekend",
      totalPriceCents: 9130,
      currency: "USD",
      availability: "InStock",
      url: "https://www.eventeny.com/events/ticket/?id=23176",
      validThrough: "2026-08-28",
    },
    {
      name: "Gold Badge",
      totalPriceCents: 17089,
      currency: "USD",
      availability: "InStock",
      url: "https://www.eventeny.com/events/ticket/?id=23176",
      validThrough: "2026-08-28",
    },
    {
      name: "Platinum Badge",
      totalPriceCents: 25617,
      currency: "USD",
      availability: "InStock",
      url: "https://www.eventeny.com/events/ticket/?id=23176",
      validThrough: "2026-08-28",
    },
  ],
  publicDates: {
    startsOn: "2026-09-04",
    endsOn: "2026-09-06",
    startsAt: "2026-09-04T12:00:00-04:00",
    endsAt: "2026-09-06T12:00:00-04:00",
    label: "September 4–6, 2026",
  },
  attendance: {
    minimum: 17000,
    label: "17K+",
  },
  venues: {
    primary: {
      name: "Greater Columbus Convention Center",
      streetAddress: "400 N High St",
      addressLocality: "Columbus",
      addressRegion: "OH",
      postalCode: "43215",
      addressCountry: "US",
    },
    secondary: {
      name: "Hyatt Regency Columbus",
      streetAddress: "350 N High St",
      addressLocality: "Columbus",
      addressRegion: "OH",
      postalCode: "43215",
      addressCountry: "US",
    },
  },
  applications: {
    artistAlley: { status: "closed" },
    panelist: { status: "closed", closesOn: "2026-07-12" },
    vendor: { status: "open" },
    volunteer: { status: "closed", closesOn: "2026-06-30" },
  },
} as const satisfies EventConfig;

export const EVENT_CONFIGS = {
  2026: EVENT_CONFIG_2026,
  // Supports the published development edition. Complete the other annual
  // website content before promoting this edition to production.
  2027: {
    ...EVENT_CONFIG_2026,
    year: 2027,
    name: "Matsuricon 2027",
    description:
      "Matsuricon 2027 brings anime, gaming, cosplay, and Japanese pop culture to Columbus, Ohio, September 2–5.",
    contentReviewedOn: "2026-10-09",
    scheduleUrl: undefined,
    registrationOffers: [],
    seoImages: {
      square: { ...EVENT_CONFIG_2026.seoImages.square, alt: "Matsuricon 2027 convention social card" },
      standard: { ...EVENT_CONFIG_2026.seoImages.standard, alt: "Matsuricon 2027 convention social card" },
      wide: { ...EVENT_CONFIG_2026.seoImages.wide, alt: "Matsuricon 2027 convention social card" },
    },
    publicDates: {
      startsOn: "2027-09-02",
      endsOn: "2027-09-05",
      startsAt: "2027-09-02T12:00:00-04:00",
      endsAt: "2027-09-05T17:00:00-04:00",
      label: "September 2–5, 2027",
    },
    // No 2027 application windows have been reviewed yet. Keep them disabled
    // rather than inheriting the previous edition's open vendor application.
    applications: {
      artistAlley: { status: "not-open" },
      panelist: { status: "not-open" },
      vendor: { status: "not-open" },
      volunteer: { status: "not-open" },
    },
  },
} as const satisfies Record<number, EventConfig>;

export type EventYear = keyof typeof EVENT_CONFIGS;

export const CURRENT_EVENT_YEAR = 2026 satisfies EventYear;

export function isEventYear(year: number): year is EventYear {
  return Object.hasOwn(EVENT_CONFIGS, year);
}

export function getEventConfig(year: number): EventConfig {
  if (!isEventYear(year)) {
    throw new Error(`No code-owned event configuration exists for Matsuricon ${year}.`);
  }

  return EVENT_CONFIGS[year];
}
